/**
 * API клієнт для doc.processing_services_sales — Реалізація послуг з переробки (виконавець).
 * Permission: production.processing.service.manage.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface ProcessingServicesSalesMaterialIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  declared_value?: string | null;
}

export interface ProcessingServicesSalesMaterial extends ProcessingServicesSalesMaterialIn {
  id: string;
}

export interface ProcessingServicesSalesProductIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
}

export interface ProcessingServicesSalesProduct extends ProcessingServicesSalesProductIn {
  id: string;
}

export interface ProcessingServicesSalesListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  warehouse_id: string;
  service_amount: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface ProcessingServicesSales extends ProcessingServicesSalesListItem {
  contract_id: string | null;
  vat_amount: string;
  revenue_account_code: string | null;     // Счет доходов (деф.703)
  receivable_account_code: string | null;  // Счет расчетов (деф.361)
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  materials: ProcessingServicesSalesMaterial[];
  products: ProcessingServicesSalesProduct[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ProcessingServicesSalesCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  customer_contractor_id: string;
  contract_id?: string | null;
  warehouse_id: string;
  service_amount: string;
  vat_amount?: string;
  revenue_account_code?: string | null;     // Счет доходов (деф.703)
  receivable_account_code?: string | null;  // Счет расчетов (деф.361)
  comment?: string | null;
  project_id?: string | null;
  materials?: ProcessingServicesSalesMaterialIn[];
  products?: ProcessingServicesSalesProductIn[];
}

export type ProcessingServicesSalesUpdate = Partial<Omit<ProcessingServicesSalesCreate, 'organization_id'>>;

export const processingServicesSales = {
  list: (params?: {
    organization_id?: string;
    customer_contractor_id?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<ProcessingServicesSalesListItem>>('/processing-services-sales', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<ProcessingServicesSales>(`/processing-services-sales/${id}`).then((r) => r.data),
  create: (payload: ProcessingServicesSalesCreate) =>
    api.post<ProcessingServicesSales>('/processing-services-sales', payload).then((r) => r.data),
  update: (id: string, payload: ProcessingServicesSalesUpdate) =>
    api.patch<ProcessingServicesSales>(`/processing-services-sales/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/processing-services-sales/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<ProcessingServicesSales>(`/processing-services-sales/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<ProcessingServicesSales>(`/processing-services-sales/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/processing-services-sales/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
